import { RoomSession } from '../../../../../models/Room'
import { ScopedBlock } from '../../../../../components/ScopedBlock/ScopedBlock'
import { TrackEvent, useTracking } from '../../common/tracking'
import { MissingScopeAction } from '../../../../../components/ScopedBlock/models'
import { Stack } from 'react-bootstrap'
import React, { useMemo, useState } from 'react'
import { supportedCallSolutions } from '../../../../../common/integrations'
import { useSessionAvailability } from '../../../../../components/ScopedBlock/hooks'
import { MeetingProviderAccountIntegrationConfig } from '../../../../../models/Account'
import { ManageCallModal } from '../../../../../views/Upcoming/ManageCallModal'
import { useAppSelector } from '../../../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../../../store/auth/authSlice'
import { parseMetadata } from '../../../../../common/util'
import { Button } from '../../../../../components/Button/Button'

export interface StartPhoneButtonsProps {
  phoneConfig: MeetingProviderAccountIntegrationConfig | null
  session: RoomSession | null
}

export const StartPhoneButtons: React.FC<StartPhoneButtonsProps> = ({ phoneConfig, session }) => {
  const { tracked } = useTracking()
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const { source } = useMemo(() => parseMetadata(session?.metadata ?? '{}'), [session])
  const [showManageCallModal, setShowManageCallModal] = useState(false)
  const [doCall, setDoCall] = useState(false)
  const provider = useMemo(() => {
    return supportedCallSolutions.find((integration) => integration.id === phoneConfig?.defaultProvider) ?? null
  }, [phoneConfig])
  const doubleAction = provider?.id && provider?.id !== 'intulse'
  const { available: sessionAvailable, isSuccess: sessionAvailabilitySuccess } = useSessionAvailability()

  const atRoomSessionLimitActions = () => {
    return sessionAvailabilitySuccess && sessionAvailable
  }

  const handleShowManageCallModal = tracked(
    ({ doCall }: { doCall: boolean }) => {
      setDoCall(doCall)
      setShowManageCallModal(true)
    },
    { eventDetails: { provider: { id: provider?.id ?? null, displayName: provider?.displayName ?? 'No Provider' } } },
    { eventNameExtractor: ({ doCall }) => (doCall ? TrackEvent.PHONE_START_CALL : TrackEvent.PHONE_ADD_TO_ONGOING_CALL) },
  )

  const handleHideManageCallModal = () => {
    setShowManageCallModal(false)
  }

  if (source !== 'phone') {
    return null
  }

  const disabled =
    session?.roomCreatedBy?.id !== currentUserId ||
    session.isActive ||
    new Date(session.startedAt).toDateString() !== new Date().toDateString() ||
    !session.finishedAt ||
    session.finishedAt <= new Date().toISOString()

  return (
    <>
      {showManageCallModal && phoneConfig && session && (
        <ManageCallModal
          session={session}
          show={showManageCallModal}
          phoneConfig={phoneConfig}
          doCall={doCall}
          showOpenMeeting={false}
          onHide={handleHideManageCallModal}
        />
      )}
      <ScopedBlock
        scope={{
          scope: 'room_session:start',
          superScope: ['room_session:admin', 'room:admin'],
        }}
        authActions={[atRoomSessionLimitActions]}
        missingScopeAction={MissingScopeAction.DISABLE}
      >
        <Stack direction='horizontal' gap={2}>
          {doubleAction && (
            <Button variant='primary' className='button-medium text-nowrap' disabled={disabled} onClick={() => handleShowManageCallModal({ doCall: true })}>
              Start Call
            </Button>
          )}
          <Button
            variant={doubleAction ? 'primaryGhost' : 'primary'}
            className='button-medium text-nowrap'
            disabled={disabled}
            onClick={() => handleShowManageCallModal({ doCall: false })}
          >
            {doubleAction ? 'Add to ongoing call' : provider?.id === 'intulse' ? 'Intulse Call' : 'Start Call'}
          </Button>
        </Stack>
      </ScopedBlock>
    </>
  )
}
